import { formatDate, formatDateTime } from '../../../../utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onJourneySubmitted } from '../../../../utils/journeySubmitSignal';
import { nextStepPathAfter, toServiceAgentPath } from '../../../../utils/journeySteps';
import { investorService } from '../../../../services/investor.service';
import { profileService, JourneyReadinessDto, SubmissionReceiptDto } from '../../../../services/profile.service';
import { SharedFormContext } from '../shared/types';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { getActiveInvestorJourney } from '../../../../services/investorJourneyScope';
import { getSAProxyInvestorId, getSAProxyJourneyId } from '../../../../services/saProxyAdapter';
import { serviceAgentService, ApprovalRequestStatus } from '../../../../services/serviceAgent.service';
import { toast } from 'react-toastify';

interface FinalSubmissionFormProps {
  onSave: () => void;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const FinalSubmissionForm: React.FC<FinalSubmissionFormProps> = ({
  onSave,
  canSubmit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    agreementComplete: false,
    agreementLegal: false,
    agreementModification: false,
  });
  const [loading, setLoading] = useState(true);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  // C2: per-journey readiness gate. When a journey is active this drives "already submitted" and whether
  // Final Submit is allowed; missing[] lists the incomplete stages.
  const [readiness, setReadiness] = useState<JourneyReadinessDto | null>(null);
  // C7: the journey's submission receipt/history (all versions).
  const [receipts, setReceipts] = useState<SubmissionReceiptDto[]>([]);

  // UAT 23 — agent WITHOUT submit rights: this tab becomes a status panel. Onboarding Only
  // gets "Submit for Approval" (asks the investor to review and submit); the lower access
  // levels only read the status. Agents with Full Onboarding keep the investor's flow below.
  const agentCannotSubmit = delegationPerms.isProxyMode && !delegationPerms.canSubmitForms;
  const approvalFlow = agentCannotSubmit && delegationPerms.scope === 'ONBOARDING_ONLY';
  const [approval, setApproval] = useState<ApprovalRequestStatus | null>(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!approvalFlow) return;
    const saInvestorId = getSAProxyInvestorId();
    if (saInvestorId === null) return;
    serviceAgentService.getInvestorApprovalRequest(saInvestorId, getSAProxyJourneyId())
      .then(setApproval)
      .catch(() => setApproval(null)); // status only — never block the tab
  }, [approvalFlow]);

  const handleRequestApproval = async () => {
    const saInvestorId = getSAProxyInvestorId();
    if (saInvestorId === null || requesting) return;
    setRequesting(true);
    try {
      const result = await serviceAgentService.requestInvestorApproval(saInvestorId, getSAProxyJourneyId());
      setApproval(result);
      toast.success(result.justSent
        ? 'The investor has been asked to review and submit.'
        : 'The investor was already asked to review and submit.');
    } catch (err: any) {
      const missing: string[] = err?.response?.data?.missing ?? [];
      toast.error(err?.response?.status === 409 && missing.length
        ? `Not ready yet — complete: ${missing.join(', ')}`
        : err?.response?.data?.message || 'Could not send the request.');
    } finally {
      setRequesting(false);
    }
  };

  // Load existing consents data + per-journey readiness + submission receipts
  useEffect(() => {
    const loadConsents = async () => {
      try {
        const [consents, r, rc] = await Promise.all([
          profileService.getConsents().catch(() => null),
          profileService.getFinalSubmitReadiness().catch(() => null),
          profileService.getSubmissionReceipts().catch(() => []),
        ]);
        setReceipts(rc || []);
        // Pre-tick the declarations only when THIS journey has already been submitted.
        // The consents row is identity-level (stored at journey 1's submit), so without this
        // gate a freshly introduced journey opened with every declaration already ticked —
        // each journey's submission is its own act of consent.
        if (consents && r?.alreadySubmitted && !r?.editUnlocked) {
          setFormData({
            agreementComplete: consents.informationCorrectConsent || false,
            agreementLegal: consents.legalCapacityConsent || false,
            agreementModification: consents.modificationAwarenessConsent || false,
          });
        }
        setReadiness(r);

        // Per-journey "already submitted" when a journey is in scope; else fall back to the
        // identity-level flag (investor.verifyStatus → InvestorBasicInfo.status).
        const dashboardData = sharedContext.dashboardData;
        // editUnlocked = the SP approved a re-edit request, so this journey is open again and the
        // live form must render instead of the read-only "Profile Already Submitted" panel.
        if (r?.journeyId) {
          setAlreadySubmitted(!!r.alreadySubmitted && !r.editUnlocked);
        } else {
          setAlreadySubmitted(dashboardData?.investor?.status === 1 && !r?.editUnlocked);
        }
      } catch (error) {
        console.error('Error loading consents:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConsents();
  }, [sharedContext.dashboardData]);

  // Gate: when a journey is scoped, block submit until it's ready. Identity-level (no journey) stays ungated.
  const journeyScoped = !!readiness?.journeyId;
  const notReady = journeyScoped && readiness?.ready === false;
  const missing = readiness?.missing ?? [];
  // C6: the SP sent this submission back / rejected it — reopen the form for re-edit + resubmit.
  const reeditable = !!readiness?.reeditable;
  // Reopened by an approved re-edit request rather than by an SP send-back — same live form, but a
  // different explanation: nothing was rejected, and there is no spReason to show.
  const editUnlocked = !!readiness?.editUnlocked;
  const spReason = readiness?.spReason;

  // Declarations live HERE on the tab (user 2026-08-26): the investor ticks all three
  // before opening the preview; the PDF page's Submit is then a plain are-you-sure prompt
  // (no repeated checkboxes). The ticked state rides to the new tab as decl=1 — appended
  // ONLY when all three are ticked, so the preview page can never submit unattested.
  const allDeclarationsTicked =
    formData.agreementComplete && formData.agreementLegal && formData.agreementModification;

  // The preview tab (Generate Preview) owns the Submit button. When it reports success,
  // this tab is the one the investor started in — move it to the journey page so they are
  // not left looking at the stale Final-Submit form in a second tab.
  useEffect(() => onJourneySubmitted(async (s) => {
    // Information is now complete: continue to the NEXT incomplete step (first submit → KYC
    // Docs; a re-submit with everything else done stays on the journey page). Decided from a
    // fresh dashboard read — the one held in this tab predates the submit.
    const fresh = await investorService.getDashboard(s.journeyId ?? undefined).catch(() => null);
    const target = nextStepPathAfter('information', fresh, s.journeyId);
    const saInvestorId = getSAProxyInvestorId();
    navigate(saInvestorId !== null ? toServiceAgentPath(target, saInvestorId) : target);
  }), [navigate]);

  const handlePrintPreview = () => {
    // H-20: carry the journey in the URL — sessionStorage does not reliably transfer to
    // the new tab, and the PDF must be THIS journey's (Print/Download journey-scoped).
    // In SA proxy mode the new tab must ALSO carry the proxy context: the adapter's
    // in-memory investor id dies with the tab, so open the SA-scoped route (its wrapper
    // re-enters proxy mode from the URL). Opening /investor/profile-pdf there sent the
    // agent's token to investor endpoints → 401 → B2C bounce (seen live 2026-09-05).
    const saInvestorId = getSAProxyInvestorId();
    const jid = (saInvestorId !== null ? getSAProxyJourneyId() : null) || getActiveInvestorJourney();
    const params = new URLSearchParams();
    if (jid) params.set('journeyId', jid);
    if (allDeclarationsTicked) params.set('decl', '1');
    const qs = params.toString();
    const base = saInvestorId !== null
      ? `/service-agent/investors/${saInvestorId}/profile-pdf`
      : '/investor/profile-pdf';
    window.open(qs ? `${base}?${qs}` : base, '_blank');
  };

  if (loading) {
    return (
      <div className="investor-profile__card">
        <h3>{approvalFlow ? 'Submit for Approval' : 'Final Submit'}</h3>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="investor-profile__card">
      <div className="form-group form-group--full mb-3">
        <h3>{approvalFlow ? 'Submit for Approval' : 'Final Submit'}</h3>
      </div>

      {alreadySubmitted ? (
        <>
          <div className="submission-submitted-alert">
            <i className="bi bi-check-circle-fill alert-icon" />
            <div className="alert-content">
              <strong>Profile Already Submitted</strong>
              <p>Your profile has been successfully submitted. Any modifications will require a Request for Change.</p>
            </div>
          </div>

          {/* The investor's ticked declarations: shown back to the investor and to a Full
              Onboarding agent; an agent who cannot submit never sees them (user 2026-10-13). */}
          {!agentCannotSubmit && (
          <div className="investor-profile__grid investor-profile__final-submit">
            <div className={`form-group form-group--checkbox form-group--full ${formData.agreementComplete ? 'checked' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreementComplete}
                  disabled={true}
                />
                I confirm that all information provided is true, complete and up to date.
              </label>
            </div>
            <div className={`form-group form-group--checkbox form-group--full ${formData.agreementLegal ? 'checked' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreementLegal}
                  disabled={true}
                />
                I am acting on my own behalf and have legal capacity to contract.
              </label>
            </div>
            <div className={`form-group form-group--checkbox form-group--full ${formData.agreementModification ? 'checked' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreementModification}
                  disabled={true}
                />
                I am aware that once I click <strong>Submit</strong>, any modification thereafter will require me to submit Request for Change.
              </label>
            </div>
          </div>
          )}

          <div className="investor-profile__buttons">
            <button
              type="button"
              className="btn-outline-primary"
              onClick={handlePrintPreview}
            >
              <i className="bi bi-file-earmark-pdf me-2" />
              View PDF
            </button>
          </div>
        </>
      ) : (
        <>
          {editUnlocked && !reeditable && (
            <div className="alert alert-success mt-1" role="alert">
              <strong>Editing unlocked.</strong> Your re-edit request was approved.
              <div className="small mt-1">Make your changes, tick the declarations again and resubmit — this creates a new version and your previous submission is preserved. The journey locks again once you resubmit.</div>
            </div>
          )}

          {reeditable && (
            <div className="alert alert-danger mt-1" role="alert">
              <strong>Sent back for changes.</strong>
              {spReason ? <> Service provider's note: <em>{spReason}</em></> : null}
              <div className="small mt-1">Make the requested changes below, then resubmit — this creates a new version and your previous submission is preserved.</div>
            </div>
          )}

          <div className="submission-steps-container form-group--full">
            <div className="submission-step-card">
              <div className="step-badge">1</div>
              <div className="step-content">
                <h4>Confirm the declarations</h4>
                <p>
                  {agentCannotSubmit
                    ? 'The investor confirms the declarations when they submit.'
                    : 'Tick the three declarations below — they enable Submit on the preview page.'}
                </p>
              </div>
            </div>
            <div className="submission-step-card">
              <div className="step-badge">2</div>
              <div className="step-content">
                <h4>{approvalFlow ? 'Review & Submit for Approval' : 'Review & Submit'}</h4>
                <p>
                  {approvalFlow
                    ? 'Open the preview and review the details, then click "Submit for Approval" — the investor is asked to review and submit the journey.'
                    : 'Open the PDF preview, review your details carefully, then click "Submit" on the preview page to finalize your journey.'}
                </p>
              </div>
            </div>
          </div>

          {notReady && (
            <div className="alert alert-warning mt-3" role="alert">
              <strong>Not ready to submit yet.</strong> Please complete the following before final submission:
              <ul className="mb-0 mt-1">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {/* The declarations are the INVESTOR's statements. An agent who cannot submit
              (Onboarding Only / CKYC Only / View Only) never acts on them — Submit for
              Approval does not read them and the preview refuses Submit — so they are not
              shown to that agent (user 2026-10-13). The investor, and a Full Onboarding
              agent who submits on their behalf, tick them as before. */}
          {!agentCannotSubmit && (
          <div className="investor-profile__grid investor-profile__final-submit mt-3">
            <div className={`form-group form-group--checkbox form-group--full ${formData.agreementComplete ? 'checked' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreementComplete}
                  onChange={(e) => setFormData({ ...formData, agreementComplete: e.target.checked })}
                />
                I confirm that all information provided is true, complete and up to date.
              </label>
            </div>
            <div className={`form-group form-group--checkbox form-group--full ${formData.agreementLegal ? 'checked' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreementLegal}
                  onChange={(e) => setFormData({ ...formData, agreementLegal: e.target.checked })}
                />
                I am acting on my own behalf and have legal capacity to contract.
              </label>
            </div>
            <div className={`form-group form-group--checkbox form-group--full ${formData.agreementModification ? 'checked' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.agreementModification}
                  onChange={(e) => setFormData({ ...formData, agreementModification: e.target.checked })}
                />
                I am aware that once I click <strong>Submit</strong>, any modification thereafter will require me to submit Request for Change.
              </label>
            </div>
          </div>
          )}

          {/* UAT 23 — agents without submit rights: Preview only (no download/print on the
              preview page), and for Onboarding Only a "Submit for Approval" that asks the
              investor to review and submit. The declarations above stay as the investor sees
              them; the investor ticks them when submitting. Full Onboarding and the investor
              keep the existing button. */}
          {agentCannotSubmit ? (
            <>
              {approvalFlow && approval?.requested && (
                <div className="submission-submitted-alert mt-3" role="status">
                  <i className="bi bi-send-check-fill alert-icon" />
                  <div className="alert-content">
                    <strong>Sent to the investor for approval</strong>
                    <p>The investor reviews the information and submits it from their own login.</p>
                  </div>
                </div>
              )}
              <div className="investor-profile__buttons">
                <button type="button" className="btn-outline-primary" onClick={handlePrintPreview}>
                  <i className="bi bi-file-earmark-pdf me-2" />
                  Preview
                </button>
                {approvalFlow && !approval?.requested && (
                  <button
                    type="button"
                    className="btn-save ms-2"
                    onClick={handleRequestApproval}
                    disabled={notReady || requesting}
                    title={notReady ? 'Complete the items listed above first' : undefined}
                  >
                    <i className="bi bi-send me-2" />
                    {requesting ? 'Sending…' : 'Submit for Approval'}
                  </button>
                )}
              </div>
              {!approvalFlow && (
                <small className="text-muted d-block mt-2">
                  Your access level does not include submitting; the investor, or an agent with Full Onboarding access, submits this journey.
                </small>
              )}
            </>
          ) : (
            <div className="investor-profile__buttons">
              <button
                type="button"
                className="btn-save"
                onClick={handlePrintPreview}
              >
                <i className="bi bi-file-earmark-pdf me-2" />
                Generate PDF Preview &amp; Submit
              </button>
            </div>
          )}
        </>
      )}

      {/* Submission history — hidden from the investor's Final Submit tab.
          Version / ROUTED / checksum are internal audit detail, not something the investor
          acts on. The receipts are still fetched and still exist in journey_submission, so
          re-enabling this is just uncommenting the block.

      {receipts.length > 0 && (
        <div className="mt-4">
          <h5 className="mb-2" style={{ fontSize: 14, fontWeight: 700 }}>Submission history</h5>
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm align-middle" style={{ fontSize: 12 }}>
              <thead>
                <tr className="text-muted">
                  <th>Version</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Delivered</th>
                  <th title="SHA-256 of the frozen submission package">Checksum</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => (
                  <tr key={r.version}>
                    <td className="fw-bold">v{r.version}</td>
                    <td>
                      {r.status}
                      {r.spReason ? <div className="small text-muted">{r.spReason}</div> : null}
                    </td>
                    <td>{r.submittedAt ? formatDateTime(r.submittedAt) : '—'}</td>
                    <td>{r.delivered ? (r.routedAt ? formatDate(r.routedAt) : 'Yes') : 'Pending'}</td>
                    <td><code title={r.checksum || ''}>{r.checksum ? r.checksum.substring(0, 10) + '…' : '—'}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      */}
    </div>
  );
};
